import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { History, GraduationCap, Sparkles } from 'lucide-react';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Landing } from '@/components/Landing';
import { Onboarding, hasSeenOnboarding } from '@/components/Onboarding';
import { FocusScreen } from '@/components/FocusScreen';
import { TestScreen } from '@/components/TestScreen';
import { SummaryScreen } from '@/components/SummaryScreen';
import { SessionHistoryScreen } from '@/components/SessionHistoryScreen';
import { TrainingDashboard } from '@/components/TrainingDashboard';
import { TrainingModulePage } from '@/components/TrainingModulePage';
import { PaymentSuccess } from '@/components/PaymentSuccess';
import { TelepathyMode } from '@/components/TelepathyMode';
import { TrainingHub } from '@/components/TrainingHub';
import { MeditationRoom } from '@/components/MeditationRoom';
import { ColorTraining } from '@/components/ColorTraining';
import { NumberTraining } from '@/components/NumberTraining';
import { FeedbackPage } from '@/components/FeedbackPage';
import { AboutPage } from '@/components/AboutPage';
import { SessionHistory } from '@/services/sessionHistory';
import { trackEvent } from '@/services/eventTracker';
import '@/index.css';

// Initialize Capacitor plugins (only on native platforms)
const initCapacitor = async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { SplashScreen } = await import('@capacitor/splash-screen');
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      
      await SplashScreen.hide();
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#0f0a1e' });
    }
  } catch (e) {
    // Not on native platform, ignore
  }
};

initCapacitor();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCREENS = {
  LANDING: 'landing',
  ONBOARDING: 'onboarding',
  FOCUS: 'focus',
  TEST: 'test',
  SUMMARY: 'summary',
  HISTORY: 'history',
  TRAINING: 'training',
  TRAINING_MODULE: 'training_module',
  TELEPATHY: 'telepathy',
  TRAINING_HUB: 'training_hub',
  MEDITATION: 'meditation',
  COLOR_TRAINING: 'color_training',
  NUMBER_TRAINING: 'number_training',
  FEEDBACK: 'feedback',
  ABOUT: 'about'
};

// Google Analytics page view tracker
const trackPageView = (pageName) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: `/${pageName}`
    });
  }
};

function MainApp() {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.LANDING);
  const [session, setSession] = useState(null);
  const [savedSession, setSavedSession] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHistoryButton, setShowHistoryButton] = useState(true);
  const [showTrainingButton, setShowTrainingButton] = useState(true);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  
  // Load session from localStorage on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem('currentSessionId');
    if (savedSessionId) {
      loadSession(savedSessionId);
    }
  }, []);
  
  const loadSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/session/${sessionId}`);
      const sessionData = response.data;
      
      if (sessionData.isComplete) {
        setSession(sessionData);
        setCurrentScreen(SCREENS.SUMMARY);
      } else {
        setSession(sessionData);
        setCurrentScreen(SCREENS.TEST);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      localStorage.removeItem('currentSessionId');
    }
  };
  
  const handleBeginSession = async (playerName, deckSize = 25) => {
    // Check if onboarding needed
    if (!hasSeenOnboarding()) {
      // Store player name and deck size temporarily
      sessionStorage.setItem('pendingPlayerName', playerName || '');
      sessionStorage.setItem('pendingDeckSize', deckSize.toString());
      setCurrentScreen(SCREENS.ONBOARDING);
      return;
    }
    
    startNewSession(playerName, deckSize);
  };
  
  const startNewSession = async (playerName, deckSize = 25) => {
    try {
      const response = await axios.post(`${API}/session`, { 
        playerName,
        deckSize
      });
      const newSession = response.data;
      
      setSession(newSession);
      localStorage.setItem('currentSessionId', newSession.sessionId);
      trackEvent.sessionStarted(newSession.sessionId);
      setCurrentScreen(SCREENS.FOCUS);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };
  
  const handleOnboardingComplete = () => {
    trackEvent.onboardingCompleted();
    const playerName = sessionStorage.getItem('pendingPlayerName') || null;
    const deckSize = parseInt(sessionStorage.getItem('pendingDeckSize') || '25');
    sessionStorage.removeItem('pendingPlayerName');
    sessionStorage.removeItem('pendingDeckSize');
    startNewSession(playerName, deckSize);
  };
  
  const handleFocusComplete = () => {
    trackEvent.focusScreenCompleted();
    setCurrentScreen(SCREENS.TEST);
  };
  
  const handleGuess = async (symbol) => {
    if (isProcessing || !session) return;
    
    setIsProcessing(true);
    trackEvent.guessMade(symbol, session.currentIndex + 1);
    
    try {
      const response = await axios.post(`${API}/guess`, {
        sessionId: session.sessionId,
        guess: symbol
      });
      
      const result = response.data;
      setLastResult(result);
      
      // Update session stats
      const updatedSession = {
        ...session,
        currentIndex: session.currentIndex + 1,
        hits: result.hits,
        misses: result.misses,
        total: result.total,
        currentStreak: result.currentStreak,
        bestStreak: result.bestStreak,
        isComplete: result.isSessionComplete
      };
      
      setSession(updatedSession);
      
      // If session complete, save to history and move to summary
      if (result.isSessionComplete) {
        const sessionToSave = {
          ...updatedSession,
          guessHistory: updatedSession.guessHistory || []
        };
        
        // Fetch full session data for history
        try {
          const fullSession = await axios.get(`${API}/session/${session.sessionId}`);
          const saved = SessionHistory.saveSession(fullSession.data);
          setSavedSession(saved);
          trackEvent.sessionCompleted(result.hits, result.hits / result.total * 100, SessionHistory.calculateLevel(result.hits));
        } catch (err) {
          console.error('Error fetching full session:', err);
        }
        
        setTimeout(() => {
          setCurrentScreen(SCREENS.SUMMARY);
          localStorage.removeItem('currentSessionId');
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error submitting guess:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleNewSession = () => {
    setSession(null);
    setSavedSession(null);
    setLastResult(null);
    localStorage.removeItem('currentSessionId');
    setCurrentScreen(SCREENS.LANDING);
  };
  
  const handleEndSession = async () => {
    if (!session) return;
    
    try {
      // Mark session as complete in backend
      const fullSession = await axios.get(`${API}/session/${session.sessionId}`);
      const saved = SessionHistory.saveSession({
        ...fullSession.data,
        isComplete: true
      });
      setSavedSession(saved);
      
      trackEvent.sessionCompleted(
        session.hits,
        (session.hits / session.total) * 100,
        SessionHistory.calculateLevel(session.hits)
      );
      
      // Move to summary
      setCurrentScreen(SCREENS.SUMMARY);
      localStorage.removeItem('currentSessionId');
    } catch (error) {
      console.error('Error ending session:', error);
      // Fallback to summary anyway
      setCurrentScreen(SCREENS.SUMMARY);
    }
  };
  
  const handleShowHistory = () => {
    trackEvent.historyViewed();
    setCurrentScreen(SCREENS.HISTORY);
  };
  
  const handleBackFromHistory = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  const handleOpenTraining = () => {
    setCurrentScreen(SCREENS.TRAINING);
  };
  
  const handleBackFromTraining = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  const handleOpenModule = (moduleId) => {
    setSelectedModuleId(moduleId);
    setCurrentScreen(SCREENS.TRAINING_MODULE);
  };
  
  const handleBackFromModule = () => {
    setCurrentScreen(SCREENS.TRAINING);
  };
  
  const handleStartSessionFromTraining = () => {
    setCurrentScreen(SCREENS.LANDING);
    // User can then start a new session from landing
  };
  
  const handleOpenTelepathy = () => {
    setCurrentScreen(SCREENS.TELEPATHY);
  };
  
  const handleBackFromTelepathy = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  // New training hub handlers
  const handleOpenTrainingHub = () => {
    setCurrentScreen(SCREENS.TRAINING_HUB);
  };
  
  const handleSelectTrainingModule = (moduleId) => {
    switch (moduleId) {
      case 'zener':
        setCurrentScreen(SCREENS.LANDING); // Go to landing for Zener test
        break;
      case 'color':
        setCurrentScreen(SCREENS.COLOR_TRAINING);
        break;
      case 'number':
        setCurrentScreen(SCREENS.NUMBER_TRAINING);
        break;
      case 'telepathy':
        setCurrentScreen(SCREENS.TELEPATHY);
        break;
      default:
        break;
    }
  };
  
  const handleBackFromTrainingHub = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  const handleOpenMeditation = () => {
    setCurrentScreen(SCREENS.MEDITATION);
  };
  
  const handleBackFromMeditation = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  const handleBackFromColorTraining = () => {
    setCurrentScreen(SCREENS.TRAINING_HUB);
  };
  
  const handleColorToNumber = () => {
    setCurrentScreen(SCREENS.NUMBER_TRAINING);
  };
  
  const handleBackFromNumberTraining = () => {
    setCurrentScreen(SCREENS.TRAINING_HUB);
  };
  
  // Feedback and About handlers
  const handleOpenFeedback = () => {
    setCurrentScreen(SCREENS.FEEDBACK);
  };
  
  const handleOpenAbout = () => {
    setCurrentScreen(SCREENS.ABOUT);
  };
  
  const handleBackFromFeedback = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  const handleBackFromAbout = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  // Universal home handler
  const handleGoHome = () => {
    setCurrentScreen(SCREENS.LANDING);
  };
  
  // Track page views on screen change
  useEffect(() => {
    trackPageView(currentScreen);
  }, [currentScreen]);
  
  // Show/hide nav buttons based on screen
  const hiddenScreens = [
    SCREENS.HISTORY, 
    SCREENS.TELEPATHY, 
    SCREENS.MEDITATION, 
    SCREENS.COLOR_TRAINING, 
    SCREENS.NUMBER_TRAINING,
    SCREENS.TRAINING_HUB
  ];
  
  useEffect(() => {
    const shouldShowHistory = !hiddenScreens.includes(currentScreen);
    const shouldShowTraining = ![...hiddenScreens, SCREENS.TRAINING, SCREENS.TRAINING_MODULE].includes(currentScreen);
    setShowHistoryButton(shouldShowHistory);
    setShowTrainingButton(shouldShowTraining);
  }, [currentScreen]);
  
  return (
    <div className="App min-h-screen relative">
      {/* Navigation Buttons (floating) - Always visible */}
      {(showHistoryButton || showTrainingButton) && (
        <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-40 flex items-center gap-2">
          {showHistoryButton && (
            <button
              onClick={handleShowHistory}
              className="flex items-center gap-2 glass-panel px-3 py-2.5 sm:px-4 sm:py-3 text-sm text-slate-300 hover:text-white transition-all hover:border-violet-500/50"
              data-testid="show-history-btn"
            >
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Geçmiş</span>
            </button>
          )}
          {showTrainingButton && (
            <button
              onClick={handleOpenTraining}
              className="flex items-center gap-2 glass-panel px-3 py-2.5 sm:px-4 sm:py-3 text-sm text-slate-300 hover:text-white transition-all hover:border-violet-500/50"
              data-testid="show-training-btn"
            >
              <GraduationCap className="w-4 h-4" />
              <span className="hidden sm:inline">Eğitim</span>
            </button>
          )}
        </div>
      )}
      
      {currentScreen === SCREENS.LANDING && (
        <Landing 
          onBeginSession={handleBeginSession} 
          onOpenTelepathy={handleOpenTelepathy}
          onOpenMeditation={handleOpenMeditation}
          onOpenTrainingHub={handleOpenTrainingHub}
          onOpenFeedback={handleOpenFeedback}
          onOpenAbout={handleOpenAbout}
        />
      )}
      
      {currentScreen === SCREENS.ONBOARDING && (
        <Onboarding onComplete={handleOnboardingComplete} />
      )}
      
      {currentScreen === SCREENS.FOCUS && (
        <FocusScreen onComplete={handleFocusComplete} />
      )}
      
      {currentScreen === SCREENS.TEST && (
        <TestScreen
          session={session}
          onGuess={handleGuess}
          lastResult={lastResult}
          onEndSession={handleEndSession}
        />
      )}
      
      {currentScreen === SCREENS.SUMMARY && (
        <SummaryScreen
          session={session}
          savedSession={savedSession}
          onNewSession={handleNewSession}
          onOpenTraining={handleOpenTraining}
          onNextStage={() => setCurrentScreen(SCREENS.COLOR_TRAINING)}
        />
      )}
      
      {currentScreen === SCREENS.HISTORY && (
        <SessionHistoryScreen onBack={handleBackFromHistory} />
      )}
      
      {currentScreen === SCREENS.TRAINING && (
        <TrainingDashboard
          onBack={handleBackFromTraining}
          onOpenModule={handleOpenModule}
          onStartSession={handleStartSessionFromTraining}
        />
      )}
      
      {currentScreen === SCREENS.TRAINING_MODULE && (
        <TrainingModulePage
          moduleId={selectedModuleId}
          onBack={handleBackFromModule}
          onStartSession={handleStartSessionFromTraining}
        />
      )}
      
      {currentScreen === SCREENS.TELEPATHY && (
        <TelepathyMode onBack={handleBackFromTelepathy} onHome={handleGoHome} />
      )}
      
      {currentScreen === SCREENS.TRAINING_HUB && (
        <TrainingHub 
          onBack={handleBackFromTrainingHub} 
          onSelectModule={handleSelectTrainingModule}
          onHome={handleGoHome}
        />
      )}
      
      {currentScreen === SCREENS.MEDITATION && (
        <MeditationRoom onBack={handleBackFromMeditation} onHome={handleGoHome} />
      )}
      
      {currentScreen === SCREENS.COLOR_TRAINING && (
        <ColorTraining 
          onBack={handleBackFromColorTraining} 
          onHome={handleGoHome}
        />
      )}
      
      {currentScreen === SCREENS.NUMBER_TRAINING && (
        <NumberTraining onBack={handleBackFromNumberTraining} onHome={handleGoHome} />
      )}
      
      {currentScreen === SCREENS.FEEDBACK && (
        <FeedbackPage onBack={handleBackFromFeedback} onHome={handleGoHome} />
      )}
      
      {currentScreen === SCREENS.ABOUT && (
        <AboutPage onBack={handleBackFromAbout} onHome={handleGoHome} />
      )}
      
      {/* Developer Panel (hidden, access via console) */}
      <div id="dev-panel" style={{ display: 'none' }} />
    </div>
  );
}

// Developer tools (access via browser console)
if (typeof window !== 'undefined') {
  window.ZenerMindDev = {
    enablePremium: () => {
      localStorage.setItem('zenermind_is_premium', 'true');
      console.log('✓ Premium enabled');
      window.location.reload();
    },
    disablePremium: () => {
      localStorage.removeItem('zenermind_is_premium');
      console.log('✓ Premium disabled');
      window.location.reload();
    },
    exportEvents: async () => {
      const { EventTracker } = await import('./services/eventTracker');
      const events = EventTracker.exportEvents();
      console.log('📊 Events:', events);
      return events;
    },
    clearHistory: async () => {
      const { SessionHistory } = await import('./services/sessionHistory');
      SessionHistory.clearHistory();
      console.log('✓ History cleared');
      window.location.reload();
    },
    resetOnboarding: () => {
      localStorage.removeItem('zenermind_has_seen_onboarding');
      console.log('✓ Onboarding reset');
      window.location.reload();
    }
  };
  console.log('🔮 Zener Mind Dev Tools loaded. Use window.ZenerMindDev');
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainApp />} />
          <Route path="/success" element={<PaymentSuccess />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
